import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api';

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com';

const HODEditIP = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const baseUrl = API_BASE_URL;

  const [formData, setFormData] = useState({
    strengths: '',
    weaknesses: '',
    employee_comment: '',
    training_needs: ''
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchPlan = async () => {
      setInitialLoading(true);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/improvement-plans/${id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = response.data || {};
        setFormData({
          strengths: data?.strengths || '',
          weaknesses: data?.weaknesses || '',
          employee_comment: data?.employee_comment || '',
          training_needs: data?.training_needs || ''
        });
      } catch (error) {
        console.error('Error fetching improvement plan:', error);
        toast.error(error.response?.data?.detail || 'Failed to load improvement plan.');
      } finally {
        setInitialLoading(false);
      }
    };

    if (id) {
      fetchPlan();
    }
  }, [id, baseUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.strengths.trim()) {
      newErrors.strengths = 'Strengths are required';
    }
    if (!formData.weaknesses.trim()) {
      newErrors.weaknesses = 'Weaknesses are required';
    }
    if (!formData.employee_comment.trim()) {
      newErrors.employee_comment = 'Employee comment is required';
    }
    if (!formData.training_needs.trim()) {
      newErrors.training_needs = 'Training needs are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      const token = Cookies.get('token');

      const submitData = new FormData();
      submitData.append('strengths', formData.strengths);
      submitData.append('weaknesses', formData.weaknesses);
      submitData.append('employee_comment', formData.employee_comment);
      submitData.append('training_needs', formData.training_needs);

      await axios.put(
        `${baseUrl}/api/improvement-plans/${id}/`,
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Improvement plan updated successfully!');
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    } catch (error) {
      console.error('Error updating improvement plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to update improvement plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <ToastContainer position="top-right" autoClose={3000} />
      <Topbar />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Header / Breadcrumbs */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2 mb-md-0">
              <div className="d-flex align-items-center gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                >
                  <i className="ti ti-arrow-left fs-16"></i>
                  <span>Back</span>
                </button>
                <div>
                  <h4 className="mb-0">Edit Improvement Plan</h4>
                  <p className="text-muted mb-0 fs-13">Update the details for this improvement plan</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Card */}
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-9">
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 border-bottom">
                  <h5 className="card-title mb-0 fw-semibold text-dark">Edit Plan Information</h5>
                </div>
                <div className="card-body p-4">
                  {initialLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mt-2">Loading improvement plan details...</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label htmlFor="strengths" className="form-label fw-medium">
                            Strengths <span className="text-danger">*</span>
                          </label>
                          <textarea
                            id="strengths"
                            name="strengths"
                            value={formData.strengths}
                            onChange={handleInputChange}
                            placeholder="Describe key strengths and areas where you excel..."
                            className={`form-control ${errors.strengths ? 'is-invalid' : ''}`}
                            rows={4}
                            disabled={loading}
                          />
                          {errors.strengths && (
                            <div className="invalid-feedback">{errors.strengths}</div>
                          )}
                        </div>

                        <div className="col-md-6">
                          <label htmlFor="weaknesses" className="form-label fw-medium">
                            Areas for Improvement / Weaknesses <span className="text-danger">*</span>
                          </label>
                          <textarea
                            id="weaknesses"
                            name="weaknesses"
                            value={formData.weaknesses}
                            onChange={handleInputChange}
                            placeholder="Identify areas for improvement and development..."
                            className={`form-control ${errors.weaknesses ? 'is-invalid' : ''}`}
                            rows={4}
                            disabled={loading}
                          />
                          {errors.weaknesses && (
                            <div className="invalid-feedback">{errors.weaknesses}</div>
                          )}
                        </div>
                      </div>

                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <label htmlFor="employee_comment" className="form-label fw-medium">
                            Employee Comment <span className="text-danger">*</span>
                          </label>
                          <textarea
                            id="employee_comment"
                            name="employee_comment"
                            value={formData.employee_comment}
                            onChange={handleInputChange}
                            placeholder="Share thoughts or suggestions about performance..."
                            className={`form-control ${errors.employee_comment ? 'is-invalid' : ''}`}
                            rows={4}
                            disabled={loading}
                          />
                          {errors.employee_comment && (
                            <div className="invalid-feedback">{errors.employee_comment}</div>
                          )}
                        </div>

                        <div className="col-md-6">
                          <label htmlFor="training_needs" className="form-label fw-medium">
                            Training Needs <span className="text-danger">*</span>
                          </label>
                          <textarea
                            id="training_needs"
                            name="training_needs"
                            value={formData.training_needs}
                            onChange={handleInputChange}
                            placeholder="Specify training or courses needed..."
                            className={`form-control ${errors.training_needs ? 'is-invalid' : ''}`}
                            rows={4}
                            disabled={loading}
                          />
                          {errors.training_needs && (
                            <div className="invalid-feedback">{errors.training_needs}</div>
                          )}
                        </div>
                      </div>

                      {/* Form Action Buttons */}
                      <div className="d-flex align-items-center justify-content-end gap-2 border-top pt-3">
                        <button
                          type="button"
                          className="btn btn-light border"
                          onClick={() => navigate(-1)}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary d-flex align-items-center gap-2"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                              <span>Updating Plan...</span>
                            </>
                          ) : (
                            <>
                              <i className="ti ti-device-floppy fs-18"></i>
                              <span>Update Improvement Plan</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HODEditIP;